// Global Instructions Rule Applied!
import React, { useMemo } from 'react'
import { CardConfig } from '../../interfaces.ts'
import { ProspectKanbanItem } from '../../../../../app_modules/fundraising/components/pipelines/kanbans/fundraising/interfaces.ts'
import { ProspectRecordFields } from '../../../../../state_manager/data_models/prospect_record.ts'
import TaskListMini from '../../../../../app_modules/tasks/components/TaskListMini.tsx'
import { InputManager } from '../../../InputManager/index.ts'
import { FieldTypeEnum } from '../../../InputManager/types.ts'
import { useCurrencies } from '../../../../../app_modules/currencies/useCurrencies.ts'
import type { ViewTaskConfig } from '../../../../../ui_configs/fund_management/view_task_config.ts'

interface UseFundraisingCardConfigParams {
  handleOpenDrawerForProspect: (prospectId: string) => void
  handleTasksClick: (allTasks: unknown[], record: unknown, taskId: string | null) => void
  handleTaskToggle: (taskId: string, recordId: string, completed: boolean) => void
  handleInvestorNameClick: (e: React.MouseEvent, accountId: string, accountName: string) => void
  handlePatchSuccess: (recordId: string, updates: Record<string, unknown>) => void
  taskConfig?: ViewTaskConfig
}

export function useFundraisingCardConfig({
  handleOpenDrawerForProspect,
  handleTasksClick,
  handleTaskToggle,
  handleInvestorNameClick,
  handlePatchSuccess,
  taskConfig
}: UseFundraisingCardConfigParams): CardConfig<ProspectKanbanItem> {
  const { getCurrencyById } = useCurrencies()

  return useMemo(
    () => ({
      getItemId: (item) => (item.id || (item as any)[ProspectRecordFields.prospects__id]) as string,
      getItemTitle: (item) =>
        (item[ProspectRecordFields.accounts__name] as string | null | undefined) || 'Unnamed Prospect',
      getItemTitleDisplay: (item) => {
        const investorName =
          (item[ProspectRecordFields.accounts__name] as string | null | undefined) || 'Unnamed Prospect'
        const accountId =
          (item[ProspectRecordFields.prospects__investor] as string | null | undefined) ||
          (item[ProspectRecordFields.accounts__id] as string | null | undefined) ||
          null

        if (accountId) {
          return (
            <span
              className='text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
              onClick={(e) => handleInvestorNameClick(e, accountId, investorName)}
            >
              {investorName}
            </span>
          )
        }

        return null
      },
      onItemEdit: (item) => {
        const prospectId = item.id || (item as any)[ProspectRecordFields.prospects__id]
        if (prospectId) handleOpenDrawerForProspect(prospectId as string)
      },
      renderItemContent: (item, column) => {
        const prospect = item as ProspectKanbanItem
        const tasks = (prospect[ProspectRecordFields.prog__tasks] as unknown[]) || []

        const leadContacts = (prospect[ProspectRecordFields.contact_prospects__lead] as any[]) || []
        const leadValue = leadContacts.map((contact: any) => {
          const contactId = contact.contacts__id || contact.id || contact.value
          const displayName =
            contact.prog__display_name ||
            `${contact.contacts__first_name || ''} ${contact.contacts__last_name || ''}`.trim() ||
            contact.contacts__email ||
            contact.label ||
            'Unknown'
          return { value: contactId, label: displayName, data: contact }
        })

        const prospectId = prospect.id || (prospect as any)[ProspectRecordFields.prospects__id]
        const currencyId = (prospect as any)[ProspectRecordFields.prospects__currency] as string | null | undefined
        const currencySymbol = getCurrencyById(currencyId)?.symbol || '$'

        const interactionDates = (prospect as any)[ProspectRecordFields.prog__account_interaction_dates] as
          | { last_interaction?: string; last_interaction_date?: string; next_meeting_date?: string; next_meeting?: string }
          | null
          | undefined
        const lastInteractionDisplay =
          interactionDates?.last_interaction || interactionDates?.last_interaction_date || null
        const nextMeetingDisplay =
          interactionDates?.next_meeting || interactionDates?.next_meeting_date || null

        const commitmentFieldConfig = {
          key: ProspectRecordFields.prospects__commitment,
          label: 'Commitment',
          type: FieldTypeEnum.CURRENCY as const,
          value: prospect[ProspectRecordFields.prospects__commitment] as number | null | undefined,
          currencySymbol,
          editable: true,
          patchEndpoint: 'prospects'
        }

        const lastInteractionFieldConfig = {
          key: ProspectRecordFields.prog__account_interaction_dates,
          label: 'Last Interaction',
          type: FieldTypeEnum.TEXT as const,
          value: lastInteractionDisplay ?? '',
          editable: false
        }

        const nextMeetingFieldConfig = {
          key: 'prog__next_meeting',
          label: 'Next Meeting',
          type: FieldTypeEnum.TEXT as const,
          value: nextMeetingDisplay ?? '',
          editable: false
        }

        const leadFieldConfig = {
          key: ProspectRecordFields.contact_prospects__lead,
          label: 'Lead',
          type: FieldTypeEnum.CONTACT as const,
          value: leadValue,
          editable: false,
          mode: 'multiple' as const,
          size: 'small' as const
        }

        const showTasks = taskConfig?.showTasks !== false
        const taskListProps = showTasks && taskConfig
          ? {
              maxVisible: taskConfig.maxVisible ?? 3,
              showCompleted: taskConfig.showCompleted ?? false
            }
          : { maxVisible: 3, showCompleted: false }

        return (
          <>
            {showTasks && tasks.length > 0 && (
              <div className='mt-1 mb-2'>
                {React.createElement(TaskListMini as React.ComponentType<any>, {
                  tasks: tasks,
                  maxVisible: taskListProps.maxVisible,
                  maxTextLength: 40,
                  showCompleted: taskListProps.showCompleted,
                  onTaskClick: (task: unknown, allTasks: unknown[], record: unknown) => {
                    handleTasksClick(allTasks, record, (task as { tasks__id: string }).tasks__id)
                  },
                  onTaskToggle: (taskId: string, recordId: string, completed: boolean) => {
                    handleTaskToggle(taskId, recordId, completed)
                  },
                  record: prospect
                })}
              </div>
            )}

            <div className='space-y-1 pt-1'>
              <div className='border-b'>
                <InputManager
                  key={`commitment-${prospectId}`}
                  inputMode='read'
                  labelPosition='inline'
                  alignment='right'
                  fields={[commitmentFieldConfig]}
                  onPatchSuccess={handlePatchSuccess}
                  enableAutoPatch={!!prospectId}
                  patchId={prospectId as string}
                  recordId={prospectId as string}
                  showLabelColons={false}
                  fieldWrapping={true}
                  textClassName='text-xs font-medium'
                />
              </div>
              <InputManager
                key={`last-interaction-${prospectId}`}
                inputMode='read'
                labelPosition='inline'
                alignment='right'
                fields={[lastInteractionFieldConfig]}
                showLabelColons={false}
                fieldWrapping={true}
                textClassName='text-xs font-medium'
              />
              <InputManager
                key={`next-meeting-${prospectId}`}
                inputMode='read'
                labelPosition='inline'
                alignment='right'
                fields={[nextMeetingFieldConfig]}
                showLabelColons={false}
                fieldWrapping={true}
                textClassName='text-xs font-medium'
              />
              <InputManager
                key={`lead-${prospectId}`}
                inputMode='read'
                labelPosition='inline'
                alignment='right'
                fields={[leadFieldConfig]}
                showLabelColons={false}
                fieldWrapping={true}
                textClassName='text-xs font-medium'
                maxLabelWidth={50}
              />
            </div>
          </>
        )
      }
    }),
    [
      handleOpenDrawerForProspect,
      handleTasksClick,
      handleTaskToggle,
      handleInvestorNameClick,
      handlePatchSuccess,
      getCurrencyById,
      taskConfig
    ]
  )
}
